import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

// Custom dropdowns ported 1:1 from the revised mockup (ehr-revised/index.html):
//  - `Dd`               → the `.dd` / `.dd-panel` trigger+menu (Referral Source, Contact Status)
//  - `MultiDd`          → the multi-select checkbox variant
//  - `Cd`               → the DEMOGRAPHICS `.cd` select (plain string options)
//  - `SearchableSelect` → the `.searchable-select` readonly-input variant (Salutation, State, …)
//
// Parity with the calendar module (#10, #34): on desktop these stay custom, with full
// ArrowUp/ArrowDown/Enter keyboard navigation; on a coarse-pointer (touch/mobile) device the
// single-selects fall back to a native `<select>` so phones get the OS picker. `useCoarsePointer`
// mirrors the calendar's `IS_TOUCH` guard (`enhanceDropdowns`).

export interface DdOption {
  value: string;
  label: string;
  /** Optional status/stage colour dot rendered before the label. */
  dot?: string;
  /** Extra text matched by the search box (e.g. full state name behind an abbrev). */
  search?: string;
  /** ehr#437: a heading shown above the first option of each run of the same group. */
  group?: string;
}

/** True on coarse-pointer devices (touch/mobile) — render native selects there. */
function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(pointer: coarse)');
    const on = () => setCoarse(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return coarse;
}

/**
 * Shared listbox keyboard nav (#10): tracks the active option and returns a keydown
 * handler. `count` is the currently-shown option count; `onPick(i)` selects shown[i].
 */
function useListboxKeys(
  open: boolean,
  setOpen: (v: boolean) => void,
  count: number,
  onPick: (i: number) => void,
) {
  const [active, setActive] = useState(-1);
  useEffect(() => {
    if (!open) setActive(-1);
  }, [open]);
  const clamp = (i: number) => Math.max(0, Math.min(i, count - 1));
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setOpen(true);
        setActive(0);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => clamp((a < 0 ? -1 : a) + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a < 0 ? count - 1 : clamp(a - 1)));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      onPick(active);
    }
  };
  return { active, setActive, onKeyDown };
}

/** Scroll the active option into view when it changes. */
const activeRef = (isActive: boolean) => (el: HTMLElement | null) => {
  if (isActive && el) el.scrollIntoView({ block: 'nearest' });
};

function useClickOutside(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return ref;
}

const chevron = (
  <svg className="dd-chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden>
    <path
      d="M1 1.5L6 6.5L11 1.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Native `<select>` fallback for coarse-pointer devices (single-select). */
/** ehr#437: options split into runs of the same `group` (ungrouped runs have none), in order. */
export function groupRuns(options: DdOption[]): [string | undefined, DdOption[]][] {
  const runs: [string | undefined, DdOption[]][] = [];
  for (const o of options) {
    const last = runs[runs.length - 1];
    if (last && last[0] === o.group) last[1].push(o);
    else runs.push([o.group, [o]]);
  }
  return runs;
}

function NativeSelect({
  value,
  options,
  onChange,
  placeholder,
  className = 'dd-native',
  ariaLabel,
  disabled,
}: {
  value: string;
  options: DdOption[];
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  return (
    <select
      className={className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      disabled={disabled}
    >
      <option value="">{placeholder}</option>
      {groupRuns(options).map(([group, run]) => {
        const opts = run.map((o) => (
          <option key={o.value || '__empty'} value={o.value}>
            {o.label}
          </option>
        ));
        return group ? (
          <optgroup key={`g:${group}`} label={group}>
            {opts}
          </optgroup>
        ) : (
          opts
        );
      })}
    </select>
  );
}

/**
 * ehr#60 - a floating `.dd-panel`: pinned to the viewport under the trigger (or above it near the
 * bottom of the screen), so a scrolling parent such as a modal body can't clip it. An outside scroll
 * or a resize closes it. Returns the panel's style, or null while closed or not floating.
 */
function useFloatingPanel(
  open: boolean,
  floating: boolean | undefined,
  ref: RefObject<HTMLDivElement | null>,
  setOpen: (open: boolean) => void,
) {
  const [pos, setPos] = useState<CSSProperties | null>(null);
  useLayoutEffect(() => {
    if (!open || !floating) return setPos(null);
    const trig = ref.current?.querySelector('.dd-trigger');
    if (!trig) return;
    const r = trig.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const up = below < 300 && r.top > below;
    setPos({
      position: 'fixed',
      left: r.left,
      right: 'auto',
      width: Math.max(r.width, 200),
      // .dd-multi's min-width: 100% would be the whole viewport once fixed (calendar#38).
      minWidth: 0,
      maxWidth: 'none',
      zIndex: 1300,
      ...(up ? { top: 'auto', bottom: window.innerHeight - r.top + 5 } : { top: r.bottom + 5 }),
    });
    const close = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, floating, ref]);
  return pos;
}

/** `.dd` custom dropdown — button trigger + floating `.dd-panel` menu with search. */
export function Dd({
  value,
  options,
  onChange,
  placeholder = '— Select —',
  searchable = true,
  className,
  ariaLabel,
  disabled,
  floating,
}: {
  value: string;
  options: DdOption[];
  onChange: (v: string) => void;
  placeholder?: string;
  searchable?: boolean;
  /** Extra class on the wrapper (desktop) and the native select (mobile), e.g. 'pill-select'. */
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * ehr#60 - pin the menu to the viewport (opening upward near the bottom) so a
   * scrolling parent such as a modal body can't clip it. Closes on outside scroll.
   */
  floating?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useClickOutside(() => setOpen(false));
  const pos = useFloatingPanel(open, floating, ref, setOpen);
  const coarse = useCoarsePointer();
  const sel = options.find((o) => o.value === value);
  const shown = q
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(q.toLowerCase()) ||
          (o.search ?? '').toLowerCase().includes(q.toLowerCase()),
      )
    : options;
  const { active, setActive, onKeyDown } = useListboxKeys(open, setOpen, shown.length, (i) => {
    onChange(shown[i]!.value);
    setOpen(false);
  });

  if (coarse) {
    return (
      <NativeSelect
        value={value}
        options={options}
        onChange={onChange}
        placeholder={placeholder}
        className={className ? `dd-native ${className}` : 'dd-native'}
        ariaLabel={ariaLabel}
        disabled={disabled}
      />
    );
  }

  return (
    <div
      className={`dd${open ? ' dd-open' : ''}${disabled ? ' dd-disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={ref}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        className="dd-trigger"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
          setQ('');
        }}
      >
        <span className={`dd-trigger-label${sel ? '' : ' dd-trigger-label-empty'}`}>
          {sel?.dot && <span className="dd-dot" style={{ background: sel.dot }} />}
          {sel ? sel.label : placeholder}
        </span>
        {chevron}
      </button>
      {open && (
        <div
          className="dd-panel"
          role="listbox"
          style={floating ? (pos ?? { visibility: 'hidden' }) : undefined}
        >
          {searchable && (
            <div className="dd-search-wrap">
              <input
                type="text"
                className="dd-search"
                placeholder="Search…"
                autoFocus
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActive(-1);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {shown.map((o, i) => (
            <Fragment key={o.value || '__empty'}>
              {o.group && o.group !== shown[i - 1]?.group && (
                <div className="dd-group" role="presentation">
                  {o.group}
                </div>
              )}
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                ref={activeRef(i === active)}
                className={`dd-opt${o.value === value ? ' sel' : ''}${i === active ? ' active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.dot !== undefined && <span className="dd-dot" style={{ background: o.dot }} />}
                <span className="dd-opt-label">{o.label}</span>
                {o.value === value && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1a8a66"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            </Fragment>
          ))}
          {shown.length === 0 && <div className="searchable-select-empty">No matches</div>}
        </div>
      )}
    </div>
  );
}

/**
 * `.dd` multi-select — mockup's `multiSelectDDHTML`. Trigger shows the selected
 * labels (or placeholder); the panel has an optional search, an optional
 * Select-all/Clear-all row, and checkbox options. Toggling keeps the panel open.
 * Arrow-key nav highlights options; Enter toggles the active one (panel stays open).
 * ehr#181 adds the shared Credify filter look, all opt-in: an `allRow` checkbox over the
 * list, a `clearable` footer, and `maxLabel` (more picks than that read "N selected",
 * every option reads "All").
 */
export function MultiDd({
  values,
  options,
  onChange,
  placeholder = '— Select —',
  searchable = true,
  selectAll = false,
  allRow = false,
  clearable = false,
  maxLabel,
  emptyText,
  ariaLabel,
  floating,
}: {
  values: string[];
  options: DdOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  /** Show the Select-all / Clear-all row at the top of the panel. */
  selectAll?: boolean;
  /** An "All" checkbox row over the options: ticks or clears every one. */
  allRow?: boolean;
  /** A Clear link under the options. */
  clearable?: boolean;
  /** Name at most this many picks on the trigger, then "N selected"; every option is "All". */
  maxLabel?: number;
  /** Shown in the panel when there are no options at all. */
  emptyText?: string;
  ariaLabel?: string;
  /** Pin the menu to the viewport, as Dd's `floating` (calendar#38: the CPT modal clipped it). */
  floating?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useClickOutside(() => setOpen(false));
  const pos = useFloatingPanel(open, floating, ref, setOpen);
  const set = new Set(values);
  const selectedLabels = options.filter((o) => set.has(o.value)).map((o) => o.label);
  const allPicked = options.length > 0 && options.every((o) => set.has(o.value));
  const triggerText = !selectedLabels.length
    ? placeholder
    : maxLabel === undefined
      ? selectedLabels.join(', ')
      : allPicked && options.length > 1
        ? 'All'
        : selectedLabels.length <= maxLabel
          ? selectedLabels.join(', ')
          : `${selectedLabels.length} selected`;
  const shown = q
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(q.toLowerCase()) ||
          (o.search ?? '').toLowerCase().includes(q.toLowerCase()),
      )
    : options;
  const toggle = (v: string) => {
    const nextSet = new Set(values);
    if (nextSet.has(v)) nextSet.delete(v);
    else nextSet.add(v);
    // Preserve option order in the emitted array.
    onChange(options.filter((o) => nextSet.has(o.value)).map((o) => o.value));
  };
  const allShownSelected = shown.length > 0 && shown.every((o) => set.has(o.value));
  const checkBox = (on: boolean) => (
    <span className={`dd-check-box${on ? ' on' : ''}`} aria-hidden>
      {on && (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </span>
  );
  // Enter toggles the active option but keeps the panel open (multi-select).
  const { active, setActive, onKeyDown } = useListboxKeys(open, setOpen, shown.length, (i) =>
    toggle(shown[i]!.value),
  );

  return (
    <div className={`dd dd-multi${open ? ' dd-open' : ''}`} ref={ref} onKeyDown={onKeyDown}>
      <button
        type="button"
        className="dd-trigger"
        aria-label={ariaLabel}
        onClick={() => {
          setOpen((o) => !o);
          setQ('');
        }}
      >
        <span
          className={`dd-trigger-label${selectedLabels.length ? '' : ' dd-trigger-label-empty'}`}
        >
          {triggerText}
        </span>
        {chevron}
      </button>
      {open && (
        <div
          className="dd-panel"
          role="listbox"
          aria-multiselectable
          style={floating ? (pos ?? { visibility: 'hidden' }) : undefined}
        >
          {searchable && (
            <div className="dd-search-wrap">
              <input
                type="text"
                className="dd-search"
                placeholder="Search…"
                autoFocus
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActive(-1);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {selectAll && (
            <button
              type="button"
              className="dd-select-all"
              onClick={() =>
                allShownSelected
                  ? onChange(values.filter((v) => !shown.some((o) => o.value === v)))
                  : onChange(
                      options
                        .filter((o) => set.has(o.value) || shown.some((s) => s.value === o.value))
                        .map((o) => o.value),
                    )
              }
            >
              {allShownSelected ? 'Clear all' : 'Select all'}
            </button>
          )}
          {allRow && options.length > 1 && !q && (
            <button
              type="button"
              role="option"
              aria-selected={allPicked}
              className={`dd-opt dd-opt-check dd-all-row${allPicked ? ' sel' : ''}`}
              onClick={() => onChange(allPicked ? [] : options.map((o) => o.value))}
            >
              {checkBox(allPicked)}
              <span className="dd-opt-label">All</span>
            </button>
          )}
          {shown.map((o, i) => {
            const on = set.has(o.value);
            return (
              <button
                type="button"
                role="option"
                aria-selected={on}
                key={o.value || '__empty'}
                ref={activeRef(i === active)}
                className={`dd-opt dd-opt-check${on ? ' sel' : ''}${i === active ? ' active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => toggle(o.value)}
              >
                {checkBox(on)}
                {o.dot !== undefined && <span className="dd-dot" style={{ background: o.dot }} />}
                <span className="dd-opt-label">{o.label}</span>
              </button>
            );
          })}
          {shown.length === 0 && (
            <div className="searchable-select-empty">
              {!options.length && emptyText ? emptyText : 'No matches'}
            </div>
          )}
          {clearable && options.length > 0 && (
            <div className="dd-foot">
              <button
                type="button"
                className="dd-clear"
                disabled={!values.length}
                onClick={() => onChange([])}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** `.cd` custom dropdown — ported 1:1 from the DEMOGRAPHICS V17 reference.
 *  Plain string options, no search box; used by the Demographics form selects. */
export function Cd({
  value,
  options,
  onChange,
  placeholder = 'Select…',
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  const coarse = useCoarsePointer();
  const has = options.includes(value);
  // Open upward when the field is near the bottom of the scroll area, so the
  // list is not clipped by the drawer body's overflow (#38 — Referral source).
  useEffect(() => {
    if (!open || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setUp(window.innerHeight - r.bottom < 280 && r.top > 300);
  }, [open, ref]);
  // index 0 is the placeholder ("clear"); real options start at 1.
  const { active, setActive, onKeyDown } = useListboxKeys(
    open,
    setOpen,
    options.length + 1,
    (i) => {
      onChange(i === 0 ? '' : options[i - 1]!);
      setOpen(false);
    },
  );

  // #56 - type-ahead: a printable key jumps to the first option that starts with
  // the typed buffer (mirrors a native <select>). The buffer resets after a pause.
  const typeahead = useRef({ buf: '', t: 0 });
  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && /\S/.test(e.key)) {
      const now = Date.now();
      const ta = typeahead.current;
      ta.buf = now - ta.t > 700 ? e.key : ta.buf + e.key;
      ta.t = now;
      const q = ta.buf.toLowerCase();
      const i = options.findIndex((o) => o.toLowerCase().startsWith(q));
      if (i >= 0) {
        if (!open) setOpen(true);
        setActive(i + 1);
        e.preventDefault();
      }
      return;
    }
    onKeyDown(e);
  };

  if (coarse) {
    return (
      <NativeSelect
        value={value}
        options={options.map((o) => ({ value: o, label: o }))}
        onChange={onChange}
        placeholder={placeholder}
        className="dd-native cd-native"
      />
    );
  }

  return (
    <div className={`cd${up ? ' cd-up' : ''}`} ref={ref} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className={`cd-btn${has ? '' : ' placeholder'}${open ? ' open' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        {has ? value : placeholder}
      </button>
      <div className={`cd-panel${open ? ' open' : ''}`} role="listbox">
        <div
          role="option"
          aria-selected={!has}
          ref={activeRef(active === 0)}
          className={`cd-opt${has ? '' : ' sel'}${active === 0 ? ' active' : ''}`}
          onMouseEnter={() => setActive(0)}
          onClick={() => {
            onChange('');
            setOpen(false);
          }}
        >
          {placeholder}
        </div>
        {options.map((o, i) => (
          <div
            key={o}
            role="option"
            aria-selected={o === value}
            ref={activeRef(active === i + 1)}
            className={`cd-opt${o === value ? ' sel' : ''}${active === i + 1 ? ' active' : ''}`}
            onMouseEnter={() => setActive(i + 1)}
            onClick={() => {
              onChange(o);
              setOpen(false);
            }}
          >
            {o}
          </div>
        ))}
      </div>
    </div>
  );
}

/** `.searchable-select` — readonly-input trigger + panel with a sticky search box. */
export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = '— Select —',
}: {
  value: string;
  options: DdOption[];
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useClickOutside(() => setOpen(false));
  const coarse = useCoarsePointer();
  const sel = options.find((o) => o.value === value);
  const shown = q
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(q.toLowerCase()) ||
          (o.search ?? '').toLowerCase().includes(q.toLowerCase()),
      )
    : options;
  const { active, setActive, onKeyDown } = useListboxKeys(open, setOpen, shown.length, (i) => {
    onChange(shown[i]!.value);
    setOpen(false);
  });

  if (coarse) {
    return (
      <NativeSelect value={value} options={options} onChange={onChange} placeholder={placeholder} />
    );
  }

  return (
    <div className={`searchable-select${open ? ' open' : ''}`} ref={ref} onKeyDown={onKeyDown}>
      <input
        type="text"
        className={`searchable-select-input${sel ? '' : ' empty'}`}
        readOnly
        value={sel ? sel.label : placeholder}
        onClick={() => {
          setOpen((o) => !o);
          setQ('');
        }}
      />
      <div className="searchable-select-panel">
        <input
          type="text"
          className="searchable-select-search"
          placeholder="Search…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(-1);
          }}
        />
        <div className="searchable-select-options">
          {shown.map((o, i) => (
            <div
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              ref={activeRef(i === active)}
              className={`searchable-select-option${o.value === value ? ' selected' : ''}${
                i === active ? ' active' : ''
              }`}
              onMouseEnter={() => setActive(i)}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </div>
          ))}
          {shown.length === 0 && <div className="searchable-select-empty">No matches</div>}
        </div>
        {sel && (
          <div
            className="searchable-select-clear"
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
          >
            Clear
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Free-text autocomplete combo (#11 payor picker). Unlike SearchableSelect the
 * input is editable and any value is allowed — the list is only suggestions. On
 * desktop it renders the custom `.searchable-select` panel (correctly sized +
 * anchored under the field, unlike the browser's native <datalist> popup); on a
 * coarse-pointer device it keeps a native input + <datalist> so phones get the
 * OS suggestion UI.
 */
export function Combobox({
  value,
  options,
  onChange,
  placeholder,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  const coarse = useCoarsePointer();
  const q = value.trim().toLowerCase();
  const shown = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  const { active, setActive, onKeyDown } = useListboxKeys(open, setOpen, shown.length, (i) => {
    onChange(shown[i]!);
    setOpen(false);
  });

  if (coarse) {
    return (
      <div className="searchable-select">
        <input
          type="text"
          className={`searchable-select-input searchable-select-combo${value ? '' : ' empty'}`}
          value={value}
          placeholder={placeholder}
          list={listId}
          onChange={(e) => onChange(e.target.value)}
        />
        <datalist id={listId}>
          {options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      </div>
    );
  }

  return (
    <div className={`searchable-select${open ? ' open' : ''}`} ref={ref} onKeyDown={onKeyDown}>
      <input
        type="text"
        className={`searchable-select-input searchable-select-combo${value ? '' : ' empty'}`}
        value={value}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
      />
      <div className="searchable-select-panel">
        <div className="searchable-select-options">
          {shown.map((o, i) => (
            <div
              key={o}
              role="option"
              aria-selected={o === value}
              ref={activeRef(i === active)}
              className={`searchable-select-option${o === value ? ' selected' : ''}${
                i === active ? ' active' : ''
              }`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(o);
                setOpen(false);
              }}
            >
              {o}
            </div>
          ))}
          {shown.length === 0 && <div className="searchable-select-empty">No matches</div>}
        </div>
      </div>
    </div>
  );
}
